import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import trainingApi from '../api';
import {
  LANGUAGES, LANGUAGE_CODES, DEFAULT_LANGUAGE, translate, nativeLabelFor,
} from './strings';

const TrainingLangContext = createContext(null);

// users.language is not clean historical data — it holds 'en', 'es', a few 'english'
// rows, and NULLs. Accept the long forms so a legacy profile value still routes the
// user correctly. Mirrors server/config/trainingLanguages.js.
const ALIASES = {
  english: 'en', eng: 'en',
  spanish: 'es', espanol: 'es', 'español': 'es',
  hmong: 'hm', hmoob: 'hm', hmn: 'hm',
};

export const normalizeLanguage = (raw) => {
  if (raw === null || raw === undefined) return null;
  const key = String(raw).trim().toLowerCase();
  if (!key) return null;
  if (LANGUAGE_CODES.includes(key)) return key;
  return ALIASES[key] || null;
};

/**
 * Where a language choice is remembered.
 *
 * Keyed by user id so a shared workstation never serves one person's language to the
 * next. Kiosk trainees use sessionStorage: the training kiosk is a shared tablet, so
 * a choice must not outlive the session.
 */
const storageFor = (isKiosk) => {
  try {
    return isKiosk ? window.sessionStorage : window.localStorage;
  } catch (_) {
    return null; // private mode / storage disabled
  }
};
const storageKey = (userId) => `ksp_training_lang_${userId || 'anon'}`;

export const TrainingLangProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const isKiosk = !!currentUser?.trainingKioskUser;
  const userId = currentUser?.id;

  const profileLanguage = normalizeLanguage(currentUser?.language);

  const [language, setLanguageState] = useState(() => {
    const store = storageFor(isKiosk);
    const saved = store ? normalizeLanguage(store.getItem(storageKey(userId))) : null;
    if (saved) return saved;
    // Kiosk trainees are asked every session — the tablet is shared and the choice is
    // one tap — so a profile value seeds the highlighted option but is not accepted
    // silently. Regular users keep their HQ profile language without being asked.
    return isKiosk ? null : profileLanguage;
  });

  // null means "not chosen yet": the gate is shown and API calls use English.
  const hasChosen = language !== null;
  const effectiveLanguage = language || profileLanguage || DEFAULT_LANGUAGE;

  const setLanguage = useCallback((next, { persistToProfile = true } = {}) => {
    const code = normalizeLanguage(next);
    if (!code) return;
    setLanguageState(code);

    const store = storageFor(isKiosk);
    if (store) {
      try { store.setItem(storageKey(userId), code); } catch (_) { /* storage full/blocked */ }
    }

    // Best effort: keep users.language in step so every other tile agrees, and so the
    // next session already knows. A failure here must not block training.
    if (persistToProfile) {
      trainingApi.setMyLanguage(code).catch(() => {});
    }
  }, [isKiosk, userId]);

  const t = useCallback(
    (key, vars) => translate(effectiveLanguage, key, vars),
    [effectiveLanguage]
  );

  const value = useMemo(() => ({
    language: effectiveLanguage,
    hasChosen,
    setLanguage,
    t,
    languages: LANGUAGES,
    nativeLabelFor,
    suggestedLanguage: profileLanguage || DEFAULT_LANGUAGE,
  }), [effectiveLanguage, hasChosen, setLanguage, t, profileLanguage]);

  return (
    <TrainingLangContext.Provider value={value}>
      {children}
    </TrainingLangContext.Provider>
  );
};

/**
 * Training-tile language + translator.
 * Safe to call outside the provider (falls back to English) so a component can be
 * rendered in isolation without blowing up.
 */
export const useTrainingLang = () => {
  const ctx = useContext(TrainingLangContext);
  if (ctx) return ctx;
  return {
    language: DEFAULT_LANGUAGE,
    hasChosen: true,
    setLanguage: () => {},
    t: (key, vars) => translate(DEFAULT_LANGUAGE, key, vars),
    languages: LANGUAGES,
    nativeLabelFor,
    suggestedLanguage: DEFAULT_LANGUAGE,
  };
};

export default TrainingLangContext;
